// import '@fontsource/roboto'; // Loads Roboto font
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { initKeycloak, getToken } from './utils/auth.js';
import extractTokenData from './utils/extractTokenData.js';
import { TailSpin } from 'react-loader-spinner';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme/theme';
import MainLayout from './components/layout/MainLayout';
import ConversationsPage from './pages/ConversationsPage';
import SettingsPage from './pages/SettingsPage';
import { useForkRef, useMediaQuery } from '@mui/material';
import MobileConversationsPage from './pages/mobile/ConversationsPage';
import MobileRecordingPage from './pages/mobile/RecordingPage';
import MobileSummaryPage from './pages/mobile/SummaryPage';
import { Box } from '@mui/material';
import { usePageMeta } from './utils/usePageMeta';

const WS_URL =
  process.env.NODE_ENV === 'development'
    ? 'ws://localhost:8000/ws/connection'
    : `wss://${window.location.host}/ws/connection`;

const sendStart = async (websocket, language, conv_id, promptTemplateId) => {
  if (websocket.readyState === WebSocket.OPEN)
    await websocket.send(
      JSON.stringify({
        message: {
          message_type: 'start',
          language: language,
          conversation_id: conv_id,
          prompt_template_id: promptTemplateId,
        },
      })
    );
};

const sendStop = async (websocket, selectedPrompt, conv_id) => {
  if (websocket.readyState === WebSocket.OPEN)
    await websocket.send(
      JSON.stringify({
        message: {
          message_type: 'stop',
          prompt_template_id: selectedPrompt,
          conversation_id: conv_id,
        },
      })
    );
};

const sendAudio = async (websocket, data) => {
  if (websocket.readyState === WebSocket.OPEN)
    await websocket.send(data);
};

const App = () => {
  const [recording, setRecording] = useState(false);
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState([]);
  const [summaryId, setSummaryId] = useState(null);
  const [audioFilename, setAudioFilename] = useState(null);
  const [generatingSummaryId, setGeneratingSummaryId] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wsOpen, setWSOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [language, setLanguage] = useState('de');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [audioReady, setAudioReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [wasRecording, setWasRecording] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showTranscriberErrorMessage, setShowTranscriberErrorMessage] = useState(false);
  const [showNewConversationErrorMessage, setShowNewConversationErrorMessage] = useState(false);
  const { t, i18n } = useTranslation();
  const [themeColors, setThemeColors] = useState(null);
  const [triggerUpdateConversationId, setTriggerUpdateConversationId] = useState(-1);

  const websocketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const pendingPromises = useRef({});

  const pingTimerRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const selectedConversationId = useRef(-1);


  //pingInterval (also used in backend for timeout) < pongTimeout + reconnectTimeout
  const pongTimeout = 4000;
  const pingInterval = 5000;
  const reconnectTimeout = 4000;

  var idcounter = 0;

  // Create theme based on current theme colors
  const theme = useMemo(() => createAppTheme(themeColors), [themeColors]);

  // Check if the screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const updatePageMeta = usePageMeta('Listen');

  useEffect(() => {
    const connect = () => {
      console.log("CONNECT");
      setLoading(true);
      initKeycloak()
        .then((auth) => {
          setAuthenticated(auth);
          if (!auth) {
            return;
          }

          const token = getToken();
          const tokenData = extractTokenData(token);
          updatePageMeta(tokenData.groupTitle);

          // Process theme colors
          if (tokenData.themeColors) {
            localStorage.setItem('themeColors', tokenData.themeColors);

            // Parse the theme colors and update state to trigger theme recreation
            try {
              const commaIndex = tokenData.themeColors.indexOf(',');
              if (commaIndex !== -1) {
                const light = tokenData.themeColors.substring(0, commaIndex);
                const main = tokenData.themeColors.substring(commaIndex + 1);
                setThemeColors({ light, main });
              }
            } catch (error) {
              console.error('Error parsing theme colors:', error);
            }
          }

          setUser({
            username: tokenData.preferred_username,
            firstName: tokenData.given_name,
            lastName: tokenData.family_name,
            email: tokenData.email,
            groupTitle: tokenData.groupTitle,
            themeColors: tokenData.themeColors,
          });
          const tokenBase64 = btoa(token).replace(/=+$/, '');
          websocketRef.current = new WebSocket(WS_URL, ['token', tokenBase64]);
          websocketRef.current._id = idcounter;
          idcounter = idcounter + 1;
          websocketRef.current.onopen = () => {
            console.log('WebSocket Connected', websocketRef.current._id);
            setWSOpen(true);
            setLoading(false);
            setAttempt(0);
            setGeneratingSummaryId(null);
            sendPing();
          };
          websocketRef.current.onclose = (event) => {
            console.log('WebSocket Closed', websocketRef.current._id, 'Code:', event.code, 'Reason:', event.reason, 'Clean:', event.wasClean);
            setWSOpen(false);
            clearHeartbeatTimer();
            reconnectTimerRef.current = setTimeout(() => {
              retryConnection();
            }, reconnectTimeout);
          };
          websocketRef.current.onmessage = (event) => {
            const message = JSON.parse(event.data).response;
            if (message.response_type === 'new_conversation') {
              const resolve = pendingPromises.current['startNewConversation'];
              if (resolve) {
                resolve(message);
                delete pendingPromises.current['startNewConversation'];
              }
            } else if (message.response_type === 'summary') {
              setSummaryFromWS(message.summary, message.conversation_id);
            } else if (message.response_type === 'audio_ready') {
              setAudioFilename(message.audio_url);
              setAudioReady(true);
              setShowControls(false);
            } else if (message.response_type === 'transcription_error_response') {
              console.log("transcription error", recording);
              setShowTranscriberErrorMessage(true);
              stopRecording(false);
            } else if (message.response_type === 'conversation_error_response') {
              console.log("new conversation error", recording);
              setShowNewConversationErrorMessage(true);
              stopRecording(false);
            } else if (message.response_type === 'pong_response') {
              console.log("pong");
              handlePong();
            } else {
              // Just update the messages array, word count will be calculated in the useEffect
              setMessages((prev) => [...prev, message.sentence]);
            }
          };
          websocketRef.current.onerror = (error) => {
            console.error('WebSocket Error:', error);
          };
        })
        .catch((err) => {
          console.error('Authentication failed:', err);
          setLoading(false);
          setAuthenticated(false);
        });
    };

    const retryConnection = () => {
      if (websocketRef.current) {
        console.log('retry connection:', websocketRef.current.readyState, 'id', websocketRef.current._id)
        if (websocketRef.current.readyState != WebSocket.CLOSED) {
          console.log('websocket not closed:', websocketRef.current.readyState);
          websocketRef.current.onmessage = websocketRef.current.onclose = websocketRef.current.onerror = null;
        }
      }
      connect();
    };

    const sendPing = () => {
      if (websocketRef.current.readyState === WebSocket.OPEN) {
        console.log("Sending ping...");
        websocketRef.current.send(JSON.stringify({ message: { message_type: "ping" } }));
        pongTimeoutRef.current = setTimeout(() => {
          console.warn("No pong received! Closing and reconnect WebSocket...");
          console.log("  websocket:", websocketRef.current._id, websocketRef.current.readyState);
          websocketRef.current.close();
          // websocketRef.current = null;
          reconnectTimerRef.current = setTimeout(() => {
            retryConnection();
          }, reconnectTimeout);
        }, pongTimeout);
      }
    };

    const handlePong = () => {
      if (pongTimeoutRef.current) {
        clearTimeout(pongTimeoutRef.current);
        pongTimeoutRef.current = null;
      }
      const randInterval = pingInterval / 10
      pingTimerRef.current = setTimeout(sendPing, pingInterval + Math.random() * randInterval - randInterval / 2);
    };

    const clearHeartbeatTimer = () => {
      if (pingTimerRef.current) {
        clearTimeout(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (pongTimeoutRef.current) {
        clearTimeout(pongTimeoutRef.current);
        pongTimeoutRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    connect();

    return () => {
      console.log('close websocket ', websocketRef);
      websocketRef.current?.close();
      clearHeartbeatTimer();
      if (window.recordingTimerInterval) {
        clearInterval(window.recordingTimerInterval);
        window.recordingTimerInterval = null;
      }
    };
  }, []);


  useEffect(() => {
    if (recording && selectedPrompt != '' && websocketRef != null) {
      console.log('set new prompt: ', selectedPrompt);
      const message = {
        message_type: 'setPromptTemplate',
        prompt_template_id: selectedPrompt,
      };
      websocketRef.current.send(JSON.stringify({ message }));
    }
  }, [selectedPrompt]);

  useEffect(() => {
    selectedConversationId.current = selectedConversation ? selectedConversation.id : -1;
  }, [selectedConversation]);

  const setSummaryFromWS = (summary, conversation_id) => {
    // console.log("set summary from WS:", summary, conversation_id, selectedConversationId ? selectedConversationId.current : -1)
    if (selectedConversationId && selectedConversationId.current === conversation_id) {
      setSummary(summary.trim());
    }
    setGeneratingSummaryId(null);
    setTriggerUpdateConversationId(conversation_id);
  };

  const startNewConversation = () => {
    if (recording) {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
      setRecording(false);
    }

    // Reset messages, summary, and recording-related state
    setMessages([]);
    setSummary(null);
    setSummaryId(null);
    setAudioFilename(null);

    // Reset recording duration and word count
    setRecordingDuration(0);
    setWordCount(0);

    // Clear any existing recording timer
    if (window.recordingTimerInterval) {
      clearInterval(window.recordingTimerInterval);
      window.recordingTimerInterval = null;
    }

    // Show controls for the new conversation
    setShowControls(true);

    return new Promise((resolve, reject) => {
      pendingPromises.current['startNewConversation'] = resolve;

      const message = {
        message_type: 'startNewConversation',
        short_note: 'New Conversation',
      };
      websocketRef.current.send(JSON.stringify({ message }));

      // Optional timeout to reject the promise if no response is received
      setTimeout(() => {
        if (pendingPromises.current['startNewConversation']) {
          reject(
            new Error('Timeout while waiting for new conversation response')
          );
          delete pendingPromises.current['startNewConversation'];
        }
      }, 5000); // Adjust timeout as needed
    });
  };

  const startRecording = async () => {
    if (recording) return;
    if (!messages.length) {
      setMessages([]);
      setWordCount(0);
    }
    setRecording(true);
    // setShowControls(true);
    setAudioFilename(null);
    setAudioReady(false);
    setRecordingDuration(0);

    // Start the timer to track recording duration
    const timerInterval = setInterval(() => {
      setRecordingDuration(prevDuration => prevDuration + 1);
    }, 1000);

    // Store the interval ID in a ref so we can clear it later
    if (window.recordingTimerInterval)
      clearInterval(window.recordingTimerInterval)
    window.recordingTimerInterval = timerInterval;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus',
      channelCount: 1,
      audioBitsPerSecond: 16000,
    });
    mediaRecorderRef.current = mediaRecorder;

    await sendStart(
      websocketRef.current,
      language,
      selectedConversation.id,
      selectedPrompt
    );

    mediaRecorder.ondataavailable = (event) => {
      sendAudio(websocketRef.current, event.data);
    };

    mediaRecorder.start(250);
    console.log("RECORDING STARTED");
  };

  const stopRecording = async (enableSendStop = true) => {
    console.log('STOP RECORDING');
    if (!recording && enableSendStop) return;
    setRecording(false);
    if (enableSendStop && messages.length)
      setShowControls(false);

    // Set generatingSummaryId to the current conversation ID
    // This will show the loading indicator while waiting for the summary
    if (selectedConversation && selectedConversation.id) {
      console.log("set generation summary:", selectedConversation.id);
      setGeneratingSummaryId(selectedConversation.id);
    }

    // Clear the timer interval when stopping recording
    if (window.recordingTimerInterval) {
      clearInterval(window.recordingTimerInterval);
      window.recordingTimerInterval = null;
    }

    try {
      if (enableSendStop) {
        await sendStop(
          websocketRef.current,
          selectedPrompt,
          selectedConversation.id
        );
      }

      // Stop the media recorder
      if (mediaRecorderRef.current) {
        const finalizeRecording = new Promise((resolve) => {
          const originalDataAvailableHandler = mediaRecorderRef.current.ondataavailable;

          mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
            // Send the final audio chunk
            sendAudio(websocketRef.current, event.data);
            resolve();
          }, { once: true });

          mediaRecorderRef.current.stop();
        });

        await finalizeRecording;

        // Clean up
        mediaRecorderRef.current = null;
      }

      console.log('Recording stopped successfully');
      return true;
    } catch (error) {
      console.error('Error stopping recording:', error);
      return false;
    }
  };


  if (wsOpen && wasRecording) {
    console.log('restart recording...');
    startRecording();
    setWasRecording(false);
  }
  if (!wsOpen && recording) {
    mediaRecorderRef.current.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
    setWasRecording(true);
  }

  // calculate word count whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      const totalWordCount = messages.reduce((count, msg) => {
        const words = msg.split(/\s+/).filter(word => word.length > 0);
        return count + words.length;
      }, 0);

      setWordCount(totalWordCount);
    }
  }, [messages]);

  // Helper function to check if a specific conversation is generating a summary
  const isGeneratingSummary = (conversationId) => {
    if (!conversationId) return false;
    return generatingSummaryId === conversationId;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <span>{t('common.connecting')}</span>
          <TailSpin color="#1DCEAB" height={100} width={100} />
        </Box>
      ) : authenticated ? (
        <BrowserRouter>
          <Routes>
            {isMobile ? (
              // Mobile Routes
              <>
                <Route
                  path="/"
                  element={
                    <MobileConversationsPage
                      recording={recording}
                      selectedConversation={selectedConversation}
                      startNewConversation={startNewConversation}
                      setSelectedConversation={setSelectedConversation}
                      setShowControls={setShowControls}
                      setMessages={setMessages}
                      setSummary={setSummary}
                      setSummaryId={setSummaryId}
                      setAudioFilename={setAudioFilename}
                      setPromptTemplates={setPromptTemplates}
                      setSelectedPrompt={setSelectedPrompt}
                      setLanguage={setLanguage}
                      setAudioReady={setAudioReady}
                      setWordCount={setWordCount}
                      setRecordingDuration={setRecordingDuration}
                      triggerUpdateConversationId={triggerUpdateConversationId}
                    />
                  }
                />
                <Route
                  path="/recording/:conversationId"
                  element={
                    <MobileRecordingPage
                      recording={recording}
                      messages={messages}
                      wsOpen={wsOpen}
                      language={language}
                      selectedPrompt={selectedPrompt}
                      promptTemplates={promptTemplates}
                      audioReady={audioReady}
                      startRecording={startRecording}
                      stopRecording={stopRecording}
                      setLanguage={setLanguage}
                      setSelectedPrompt={setSelectedPrompt}
                      wordCount={wordCount}
                      recordingDuration={recordingDuration}
                      setShowControls={setShowControls}
                      selectedConversation={selectedConversation}
                      setSelectedConversation={setSelectedConversation}
                    />
                  }
                />
                <Route
                  path="/summary/:conversationId"
                  element={
                    <MobileSummaryPage
                      summary={summary || ''}
                      setSummary={setSummary}
                      summaryId={summaryId}
                      setSummaryId={setSummaryId}
                      setGeneratingSummary={setGeneratingSummaryId}
                      audioFilename={audioFilename}
                      setAudioFilename={setAudioFilename}
                      conversation={selectedConversation}
                      promptTemplates={promptTemplates}
                      selectedPrompt={selectedPrompt}
                      setSelectedPrompt={setSelectedPrompt}
                      startRecording={startRecording}
                      stopRecording={stopRecording}
                      recording={recording}
                      audioReady={audioReady}
                      recordingDuration={recordingDuration}
                      wordCount={wordCount}
                      messages={messages}
                      generatingSummary={selectedConversation ? isGeneratingSummary(selectedConversation.id) : false}
                      setShowControls={setShowControls}
                      setSelectedConversation={setSelectedConversation}
                    />
                  }
                />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              // Desktop Routes
              <Route path="/" element={<MainLayout user={user} />}>
                <Route
                  index
                  element={
                    <ConversationsPage
                      recording={recording}
                      messages={messages}
                      summary={summary}
                      summaryId={summaryId}
                      audioFilename={audioFilename}
                      generatingSummary={selectedConversation ? isGeneratingSummary(selectedConversation.id) : false}
                      wsOpen={wsOpen}
                      showControls={showControls}
                      selectedConversation={selectedConversation}
                      language={language}
                      selectedPrompt={selectedPrompt}
                      promptTemplates={promptTemplates}
                      audioReady={audioReady}
                      startNewConversation={startNewConversation}
                      startRecording={startRecording}
                      stopRecording={stopRecording}
                      setLanguage={setLanguage}
                      setSelectedPrompt={setSelectedPrompt}
                      setSelectedConversation={setSelectedConversation}
                      setShowControls={setShowControls}
                      wordCount={wordCount}
                      recordingDuration={recordingDuration}
                      setSummary={setSummary}
                      setSummaryId={setSummaryId}
                      setGeneratingSummary={setGeneratingSummaryId}
                      setPromptTemplates={setPromptTemplates}
                      setMessages={setMessages}
                      setAudioFilename={setAudioFilename}
                      setAudioReady={setAudioReady}
                      setWordCount={setWordCount}
                      setRecordingDuration={setRecordingDuration}
                      showTranscriberErrorMessage={showTranscriberErrorMessage}
                      setShowTranscriberErrorMessage={setShowTranscriberErrorMessage}
                      showNewConversationErrorMessage={showNewConversationErrorMessage}
                      setShowNewConversationErrorMessage={setShowNewConversationErrorMessage}
                      triggerUpdateConversationId={triggerUpdateConversationId}
                    />
                  }
                />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            )}
          </Routes>
        </BrowserRouter>
      ) : null}
    </ThemeProvider>
  );
};

export default App;
